
import _ from 'lodash';
import AppAiIcon from '@/components/core/AppAiIcon';

export default {
  name: 'AppDropdown',
  components: {
    AppAiIcon
  },
  model: { event: 'change' },
  props: {
    value: { type: [String, Number, Boolean, Array], default: null },
    options: { type: Array, required: true },
    enableSelectAll: { type: Boolean, default: false },
    showTitleSection: { type: Boolean, default: false },
    type: {
      type: String,
      default: 'basic',
      validator: v =>
        [
          'basic',
          'radio',
          'button',
          'button_multiple',
          'button_multiple_range',
          'score',
          'customized'
        ].includes(v)
    },
    title: { type: String, default: null },
    showTitleWithOptions: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    buttonStyle: {
      type: String,
      required: true,
      validator: v => ['plain', 'underline', 'outline'].includes(v)
    },
    labelFontSize: {
      type: Number,
      default: 12
    },
    menuAlign: { type: String, default: 'left' },
    starColor: { type: String, default: null },
    starSize: { type: Number, default: 14 },
    optionFontLarge: { type: Boolean, default: false },
    menuGap: { type: Number, default: 8 },
    description: { type: String, default: '' },
    useSelectedButtonStyle: { type: Boolean, default: false },
    iconName: { type: String, default: null },
    showOnlyTitle: { type: Boolean, default: false },
    unit: { type: String, default: '' },
    menuWidth: { type: Number, default: null }
  },
  data() {
    return {
      isDropdownVisible: false,
      adjustedMenuAlign: null
    };
  },
  computed: {
    currentValue: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('change', newValue);
      }
    },
    disabledButton() {
      return (
        this.disabled || this.options.filter(o => !o.disabled).length === 0
      );
    },
    selectAllLabel() {
      return `${this.$t('all')} ${this.title}`;
    },
    visibleOptions() {
      return [
        this.enableSelectAll
          ? {
              name: this.selectAllLabel,
              disabled: false,
              default: true,
              value: null
            }
          : null,
        ...this.options
      ].filter(v => v);
    },
    isButtonSelected() {
      return (
        this.currentValue && this.selectedOptions && this.selectedOptions.length
      );
    },
    selectedOptions() {
      return this.visibleOptions.filter(o =>
        Array.isArray(this.currentValue)
          ? this.currentValue.includes(o.value)
          : o.value === this.currentValue
      );
    },
    label() {
      if (this.showOnlyTitle || !this.selectedOptions.length) return this.title;

      return this.type === 'button_multiple_range'
        ? this.multipleRangeLabel
        : this.selectedOptionLabel;
    },
    isArrowIconVisible() {
      return this.buttonStyle !== 'underline';
    },
    labelStyleProp() {
      return `--label-font-size: ${this.labelFontSize}px`;
    },
    useIconForLabel() {
      return !!this.iconName;
    },
    multipleRangeLabel() {
      const optionRange = this.selectedOptions.map(o => o.value.split('-'));
      const min = parseInt(optionRange[0][0]);
      const max = parseInt(optionRange[optionRange.length - 1][1]);

      let label = '';
      if (_.isEqual(this.selectedOptions, this.visibleOptions))
        label = this.$t('all');
      else if (!min)
        label = this.$t('range.under', { value: max - 1, unit: this.unit });
      else if (!max)
        label = this.$t('range.over', { value: min, unit: this.unit });
      else
        label = this.$t('range.between', {
          start: min,
          end: max - 1,
          unit: this.unit
        });

      return this.showTitleWithOptions && label !== this.selectAllLabel
        ? `${this.title}: ${label}`
        : label;
    },
    menuWidthProps() {
      return this.menuWidth
        ? {
            '--menu-width': `${this.menuWidth}px`
          }
        : {};
    },
    selectedOptionLabel() {
      const optionStr = _.isEqual(this.selectedOptions, this.visibleOptions)
        ? this.$t('all')
        : this.selectedOptions.map(o => o.name).join(', ');
      return this.showTitleWithOptions && optionStr !== this.selectAllLabel
        ? `${this.title}: ${optionStr}`
        : optionStr;
    },
    selectedOptionLeadingAiIconName() {
      return (
        this.selectedOptions.find(o => !!o.leadingAiIconName)
          ?.leadingAiIconName || ''
      );
    }
  },
  created() {
    this.adjustMenuAlign();
    window.addEventListener('resize', _.debounce(this.adjustMenuAlign, 100));
  },
  beforeDestroy() {
    window.removeEventListener('resize', _.debounce(this.adjustMenuAlign, 100));
  },
  methods: {
    click(e) {
      if (!e.target.closest('.AppDropdown__modeless')) {
        this.isDropdownVisible ? this.close() : this.open();
      }
    },
    open() {
      this.isDropdownVisible = true;
    },
    close() {
      this.isDropdownVisible = false;
    },
    adjustMenuAlign() {
      this.close();
      if (this.menuAlign !== 'auto') {
        this.adjustedMenuAlign = this.menuAlign;
        return;
      }

      const maxWidthForCenterAlign = 480;
      if (window.innerWidth > maxWidthForCenterAlign)
        this.adjustedMenuAlign = 'auto';
      else this.adjustedMenuAlign = 'center';
    }
  }
};
